import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from 'core/api/queryKeys';
import { ApiError } from 'core/errors/ApiError';
import { humanizeApiError, mapFieldErrors } from 'core/errors/mapFieldErrors';
import { useI18n } from 'core/i18n/I18nProvider';
import { paths } from 'core/routing/paths';
import { LEGAL_TYPES, operationsApi } from 'features/operations/operations.api';
import { Button } from 'shared/components/Button';
import { ErrorState, PageHeader, Skeleton } from 'shared/components/Feedback';
import { useConfirm } from 'shared/components/ConfirmDialog';
import { useToast } from 'shared/components/Toast';
import { BilingualField, TextField } from 'shared/forms/Fields';
import { useUnsavedChanges } from 'shared/utilities/useUnsavedChanges';

const emptyForm = {
  type: 'terms',
  version: '',
  title_ar: '',
  title_en: '',
  content_ar: '',
  content_en: '',
  effective_at: '',
};

export function LegalDocumentFormPage({ readOnly = false }) {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const location = useLocation();
  const { t, locale } = useI18n();
  const toast = useToast();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [dirty, setDirty] = useState(false);
  const [conflict, setConflict] = useState(null);
  useUnsavedChanges(dirty && !readOnly);

  const detailQuery = useQuery({
    queryKey: queryKeys.legalDocument(id),
    enabled: isEdit,
    queryFn: ({ signal }) => operationsApi.showLegalDocument(id, { signal }),
  });

  useEffect(() => {
    const seed = location.state?.fromDocument;
    if (!isEdit && seed) {
      setForm({
        type: seed.type || 'terms',
        version: '',
        title_ar: seed.title_ar || '',
        title_en: seed.title_en || '',
        content_ar: seed.content_ar || '',
        content_en: seed.content_en || '',
        effective_at: '',
      });
    }
  }, [isEdit, location.state]);

  useEffect(() => {
    const document = detailQuery.data;
    if (!document || Array.isArray(document)) return;
    setForm({
      type: document.type || 'terms',
      version: document.version || '',
      title_ar: document.title_ar || '',
      title_en: document.title_en || '',
      content_ar: document.content_ar || '',
      content_en: document.content_en || '',
      effective_at: document.effective_at ? String(document.effective_at).slice(0, 16) : '',
    });
  }, [detailQuery.data]);

  const published = detailQuery.data?.status === 'published';
  const editingLocked = readOnly || published;

  const save = useMutation({
    mutationFn: (body) => (isEdit ? operationsApi.updateLegalDocument(id, body) : operationsApi.createLegalDocument(body)),
    onSuccess: async () => {
      setDirty(false);
      setConflict(null);
      await queryClient.invalidateQueries({ queryKey: queryKeys.legalDocuments() });
      toast.success(t('status.saved'));
      navigate(paths.legalDocuments);
    },
    onError: (error) => {
      if (error instanceof ApiError && error.isValidation()) setErrors(mapFieldErrors(error.errors));
      if (error instanceof ApiError && error.isConflict()) {
        setConflict(error.message);
        return;
      }
      toast.error(humanizeApiError(error, t));
    },
  });

  const publish = useMutation({
    mutationFn: () => operationsApi.publishLegalDocument(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.legalDocuments() });
      await queryClient.invalidateQueries({ queryKey: queryKeys.legalDocument(id) });
      toast.success(t('status.published'));
    },
    onError: (error) => {
      if (error instanceof ApiError && error.isConflict()) {
        setConflict(error.message);
        return;
      }
      toast.error(humanizeApiError(error, t));
    },
  });

  const change = (key, value) => {
    setDirty(true);
    setForm((current) => ({ ...current, [key]: value }));
  };

  const onSubmit = (event) => {
    event.preventDefault();
    if (editingLocked) return;
    setErrors({});
    save.mutate({
      type: form.type,
      version: form.version,
      title_ar: form.title_ar,
      title_en: form.title_en,
      content_ar: form.content_ar,
      content_en: form.content_en,
      effective_at: form.effective_at || null,
    });
  };

  if (isEdit && detailQuery.isLoading) return <Skeleton rows={6} />;
  if (isEdit && detailQuery.isError) {
    return <ErrorState title={humanizeApiError(detailQuery.error, t)} onRetry={detailQuery.refetch} retryLabel={t('actions.retry')} />;
  }

  return (
    <form className="stack-form" onSubmit={onSubmit} noValidate>
      <PageHeader title={readOnly ? t('legal.title') : isEdit ? t('legal.title') : t('legal.create')} />
      {published ? <p className="warning-banner">{t('legal.immutable')}</p> : null}
      {conflict ? <p className="warning-banner" role="alert">{conflict} {t('conflict.keepLocal')}</p> : null}
      <label className="field">
        <span className="field-label">{t('legal.type')}</span>
        <select className="input" value={form.type} disabled={isEdit || editingLocked} onChange={(event) => change('type', event.target.value)}>
          {LEGAL_TYPES.map((type) => <option key={type} value={type}>{t(`legal.types.${type}`)}</option>)}
        </select>
      </label>
      <TextField id="version" label={t('legal.version')} value={form.version} onChange={(value) => change('version', value)} error={errors.version?.[0]} disabled={editingLocked} />
      <BilingualField
        id="legal-title"
        labelAr={t('legal.titleAr')}
        labelEn={t('legal.titleEn')}
        valueAr={form.title_ar}
        valueEn={form.title_en}
        onChangeAr={(value) => change('title_ar', value)}
        onChangeEn={(value) => change('title_en', value)}
        errorAr={errors.title_ar?.[0]}
        errorEn={errors.title_en?.[0]}
      />
      <BilingualField
        id="legal-content"
        labelAr={t('legal.contentAr')}
        labelEn={t('legal.contentEn')}
        valueAr={form.content_ar}
        valueEn={form.content_en}
        onChangeAr={(value) => change('content_ar', value)}
        onChangeEn={(value) => change('content_en', value)}
        errorAr={errors.content_ar?.[0]}
        errorEn={errors.content_en?.[0]}
        multiline
      />
      <TextField id="effective" type="datetime-local" label={t('legal.effectiveAt')} value={form.effective_at} onChange={(value) => change('effective_at', value)} disabled={editingLocked} />
      <section className="card">
        <h2>{t('legal.preview')}</h2>
        <article dir={locale === 'ar' ? 'rtl' : 'ltr'}>
          <h3>{locale === 'ar' ? form.title_ar : form.title_en}</h3>
          <pre className="preview-block">{locale === 'ar' ? form.content_ar : form.content_en}</pre>
        </article>
      </section>
      <p className="muted">{t('legal.noAcceptanceCounts')}</p>
      <div className="toolbar">
        {editingLocked ? <Link className="btn btn-secondary" to={paths.legalDocuments}>{t('actions.back')}</Link> : (
          <Button type="submit" loading={save.isPending} disabled={save.isPending}>{t('actions.save')}</Button>
        )}
        {published && !readOnly ? (
          <Link className="btn btn-primary" to={paths.legalDocumentCreate} state={{ fromDocument: detailQuery.data }}>{t('legal.newVersion')}</Link>
        ) : null}
        {!published && isEdit && !readOnly ? (
          <Button
            type="button"
            loading={publish.isPending}
            onClick={async () => {
              if (await confirm({ body: t('legal.publishConfirm') })) {
                publish.mutate();
              }
            }}
          >
            {t('legal.publish')}
          </Button>
        ) : null}
      </div>
    </form>
  );
}
